import { useNavigate } from 'react-router-dom';
import { LogOut, User, Mail, Calendar, Key, Shield, ExternalLink } from 'lucide-react';
import { useAuth } from '@/auth/auth-context';
import { Button } from '@/components/ui';
import { Card, CardContent } from '@/components/ui';

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, signOut, isLoading } = useAuth();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Not logged in</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-2xl mx-auto" data-testid="profile-page">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-testid="profile-page-title">Profile</h1>

      {/* User info card */}
      <Card className="mb-6" data-testid="user-info-card">
        <CardContent className="flex items-center gap-4">
          {user.picture ? (
            <img
              data-testid="user-avatar"
              src={user.picture}
              alt={user.name}
              className="w-20 h-20 rounded-full object-cover ring-4 ring-primary-100"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center ring-4 ring-primary-50" data-testid="user-avatar-placeholder">
              <User className="w-10 h-10 text-primary-600" />
            </div>
          )}
          <div>
            <h2 className="text-xl font-semibold text-gray-900" data-testid="user-name">{user.name}</h2>
            <p className="text-gray-500" data-testid="user-email">{user.email}</p>
            <div className="flex items-center gap-1 mt-1">
              <Shield className="w-3 h-3 text-green-500" />
              <span className="text-xs text-green-600" data-testid="auth-status">Authenticated</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account details */}
      <Card className="mb-6">
        <CardContent>
          <h3 className="font-medium text-gray-900 mb-4">Account Details</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <User className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium text-gray-900">{user.name}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Mail className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium text-gray-900">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Member since</p>
                <p className="font-medium text-gray-900">
                  {user.createdAt
                    ? new Date(user.createdAt).toLocaleDateString()
                    : 'Unknown'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Key className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">User ID</p>
                <p className="font-mono text-xs text-gray-700 bg-gray-50 px-2 py-1 rounded break-all">
                  {user.id}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data storage info */}
      <Card className="mb-6">
        <CardContent>
          <h3 className="font-medium text-gray-900 mb-2">Your Data</h3>
          <p className="text-sm text-gray-500 mb-4">
            Your conversations, tasks, and commitments are encrypted before storage and associated with your account.
            Even system administrators cannot read your personal data.
          </p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Shield className="w-4 h-4" />
              <span>Encrypted cloud storage</span>
            </div>
            <a
              href={import.meta.env.VITE_PRIVACY_DETAILS_URL || 'https://lifeguide.app/privacy'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 hover:underline"
            >
              <span>Learn how we protect your data</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Sign out */}
      <Button
        data-testid="sign-out-button"
        onClick={handleSignOut}
        variant="danger"
        isLoading={isLoading}
        leftIcon={<LogOut className="w-4 h-4" />}
        className="w-full"
      >
        Sign Out
      </Button>
    </div>
  );
}
